import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Images
import heroImage from "@/assets/block-work-retaining-wall-cedar-grove-nj.webp";
import concreteBlockImage from "@/assets/concrete-block-construction-hackensack-nj.webp";
import blockRetainingWallImage from "@/assets/block-retaining-wall-wallington-nj.webp";
import retainingWallInstallImage from "@/assets/retaining-wall-installation-rutherford-nj.webp";

// Citation Link Component
interface CitationLinkProps {
  href: string;
  children: React.ReactNode;
  number: number;
}

const CitationLink = ({ href, children, number }: CitationLinkProps) => (
  <>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-secondary hover:text-secondary/80 underline underline-offset-2"
    >
      {children}
    </a>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-secondary hover:text-secondary/80 text-xs align-super ml-0.5"
    >
      [{number}]
    </a>
  </>
);

const blockTypes = [
  {
    name: "Standard Stretcher Blocks",
    description: "The most common CMU. Hollow, lighter to handle, and ideal for general wall construction.",
  },
  {
    name: "Solid Blocks",
    description: "Carry heavier loads. Used where extra strength matters: foundations and load-bearing walls.",
  },
  {
    name: "Split-Face Blocks",
    description: "Rough-textured finish that adds a finished look without needing a veneer layer.",
  },
  {
    name: "Pillar & Column Blocks",
    description: "Shaped for structural posts, mailbox surrounds, and entryway features.",
  },
  {
    name: "Cap Blocks",
    description: "Sit on top to finish the wall and shed water. Essential for wall longevity.",
  },
];

const installationSteps = [
  {
    step: 1,
    title: "Layout & Footing",
    description: "Mark the layout and dig the footing trench to the depth local code requires.",
  },
  {
    step: 2,
    title: "Pour Footing",
    description: "Pour a concrete footing and let it cure per ASTM guidelines.",
  },
  {
    step: 3,
    title: "First Course",
    description: "Set the first course of blocks in a full mortar bed, checking level on every unit.",
  },
  {
    step: 4,
    title: "Stack Courses",
    description: "Stack remaining courses in a running bond pattern with Type S or Type N mortar.",
  },
  {
    step: 5,
    title: "Rebar & Grout",
    description: "Install steel rebar in vertical cores and fill with grout for added strength.",
  },
  {
    step: 6,
    title: "Tool Joints",
    description: "Tool the mortar joints while still damp to seal against moisture.",
  },
  {
    step: 7,
    title: "Cure & Inspect",
    description: "Cure and inspect the finished wall for plumb, level, and proper joint thickness.",
  },
];

const faqs = [
  {
    question: "What types of block work do masonry contractors build in North Jersey?",
    answer: "Foundation walls, retaining walls, garden walls, structural additions, and decorative block projects. Across Bergen, Passaic, Morris, and Essex Counties, block work is one of the most common masonry services homeowners and property managers request.",
  },
  {
    question: "How long does a block wall last in North Jersey's climate?",
    answer: "A quality block wall can last 100 years or more with proper installation and regular maintenance. In North Jersey, freeze-thaw conditions mean you need ASTM C90-rated blocks and routine mortar joint upkeep to hit that mark.",
  },
  {
    question: "Do I need a permit for block work in North Jersey?",
    answer: "Most municipalities require permits for structural block work. Retaining walls over 4 feet typically need engineered plans approved by the local building department before construction can start.",
  },
  {
    question: "What is the best mortar type for block work in North Jersey?",
    answer: "Type S mortar is the recommended choice for below-grade and exterior block walls. It handles North Jersey's freeze-thaw cycles and meets the structural load requirements for most residential and commercial block projects.",
  },
  {
    question: "How can I tell if my block wall needs repair?",
    answer: "Look for stair-step cracking in the mortar joints, white powdery residue (efflorescence), leaning, or water seepage. Any of these signs mean it's time to schedule an assessment, ideally before winter when freeze-thaw damage accelerates.",
  },
  {
    question: "Is block work better than poured concrete for retaining walls in North Jersey?",
    answer: "Both perform well for retaining walls. Block offers more design flexibility, easier section-by-section repairs, and built-in drainage options. That's a good fit for the hilly terrain common across Passaic and Morris Counties.",
  },
];

const citations = [
  {
    id: 1,
    source: "CMHA (formerly NCMA)",
    description: "\"Concrete Masonry Construction\" TEK 03-08A — industry guidance on CMU construction, durability, and best practices.",
    url: "https://www.masonryandhardscapes.org/resource/tek-03-08a/",
  },
  {
    id: 2,
    source: "CMHA",
    description: "\"CMU Shapes, Sizes, Properties, and Specifications\" — comprehensive reference for block types and performance ratings.",
    url: "https://www.masonryandhardscapes.org/resource/cmu-tec-001/",
  },
  {
    id: 3,
    source: "ASTM C90",
    description: "Standard Specification for Loadbearing Concrete Masonry Units — compressive strength and durability requirements.",
    url: "https://store.astm.org/standards/c90",
  },
  {
    id: 4,
    source: "MCAA",
    description: "Mason Contractors Association of America — mortar standards and masonry best practices.",
    url: "https://masoncontractors.org/mcaa/",
  },
  {
    id: 5,
    source: "NJ UCC Building Subcode",
    description: "NJ UCC 2018, Chapter 21: Masonry — local code requirements for structural masonry in New Jersey.",
    url: "https://up.codes/viewer/new_jersey/ibc-2018/chapter/21/masonry",
  },
];

const youtubeVideoIds = ["g25VA4vng-Y", "J8_3EVFMsvM", "BiS2ELaDD0E"];


const BlockWork = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Block Work Totowa, NJ | 35+ Years | Masonry Pro Construction</title>
        <meta property="og:description" content="Licensed masonry contractors serving Bergen, Passaic, Morris, and Essex Counties. Block work for foundations, retaining walls, garden walls, and more." />
        <meta property="og:title" content="Block Work Totowa, NJ | 35+ Years | Masonry Pro Construction" />
        <meta
          name="description"
          content="Licensed masonry contractors serving Bergen, Passaic, Morris, and Essex Counties. Block work for foundations, retaining walls, garden walls, and more."
        />
        <link rel="preload" as="image" href={heroImage} type="image/webp" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Block Work",
            description: "Block work for foundations, retaining walls, and garden walls in North Jersey.",
            url: "/masonry-services/block-work",
            image: "https://masonry-pro.com/og/block-work.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/block-work.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/block-work.jpg" />
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section
          className="relative overflow-hidden"
          style={{
            backgroundColor: '#0d1017',
            paddingTop: '160px',
            paddingBottom: '80px',
          }}
        >
          <img
            src={heroImage}
            alt="Block work retaining wall construction in Cedar Grove, NJ"
            width={1200}
            height={800}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
              <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Block Work</span> Contractor in Totowa, NJ Serving North Jersey
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
              Built to Last for Decades: Foundations, Retaining Walls, Garden Walls & More
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="xl" asChild>
                <a href="tel:+19737252395" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (973) 725-2395
                </a>
              </Button>
              <Button variant="goldOutline" size="xl" asChild>
                <a href="#contact">Get Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Intro Section with Image Placeholder */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Concrete Block Work Creates <span className="text-gradient-gold">Strong, Long-Lasting Structures</span>
                </h2>
                <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                  <p>
                    Block work is the process of stacking concrete masonry units (CMUs) with mortar to form walls and load-bearing structures. It sounds straightforward, but the materials and technique behind it are what make it last. A properly built block wall can hold up for 100 years or more <CitationLink href="https://www.masonryandhardscapes.org/resource/tek-03-08a/" number={1}>per CMHA guidelines</CitationLink>. Block work supports a lot of <Link to="/" className="text-secondary hover:underline">our masonry services</Link>, from foundations to retaining walls.
                  </p>
                  <p>
                    CMUs resist fire, insects, and rot. They don't warp. They require less maintenance than wood framing or other materials. In North Jersey, our freeze-thaw cycles put extra stress on walls every winter. That's why we use blocks rated for severe weather exposure and meeting <CitationLink href="https://store.astm.org/standards/c90" number={3}>ASTM C90 compressive strength requirements</CitationLink>.
                  </p>
                  <p>
                    Properties in Montclair and Ridgewood rely on block work for basement and foundation walls, and the durability shows decades later.
                  </p>
                </div>
              </div>
              {/* Image Placeholder 1 */}
              <div className="overflow-hidden rounded-xl border border-border bg-muted/20 aspect-[4/3]">
                <img src={concreteBlockImage} alt="Concrete block construction with rebar reinforcement in Hackensack, NJ" width={800} height={600} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* Block Types Section */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                The Right Block Type Matches <span className="text-gradient-gold">Your North Jersey Project</span>
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-3xl mx-auto">
                Not every block does the same job. Picking the right type for your project saves time and avoids problems later on.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {blockTypes.map((type, index) => (
                  <div key={index} className="p-6 bg-card rounded-xl border border-border">
                    <h3 className="text-lg font-bold mb-2 text-secondary">{type.name}</h3>
                    <p className="text-muted-foreground text-sm">{type.description}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground text-center mt-8">
                New Jersey's Uniform Construction Code (UCC) requires load-bearing walls to use blocks meeting <CitationLink href="https://store.astm.org/standards/c90" number={3}>ASTM C90 standards</CitationLink>, enforced at the municipal level <CitationLink href="https://up.codes/viewer/new_jersey/ibc-2018/chapter/21/masonry" number={5}>per NJ UCC</CitationLink>.
              </p>
            </div>
          </div>
        </section>

        {/* Installation Steps with Image Placeholder */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center lg:[direction:rtl]">
              {/* Image Placeholder 2 */}
              <div className="lg:[direction:ltr] overflow-hidden rounded-xl border border-border bg-muted/20 aspect-[4/3]">
                <img src={blockRetainingWallImage} alt="Block retaining wall construction with engineering plans in Wallington, NJ" width={800} height={600} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="lg:[direction:ltr]">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Proper Steps Keep Every Block Course <span className="text-gradient-gold">Level and Secure</span>
                </h2>
                <p className="text-muted-foreground mb-6">
                  A block wall is only as good as its base. Skip a step, rush the footing, or ignore the soil conditions, and you'll see cracks within a couple of years. An experienced crew lays roughly 100 to 120 blocks per day under normal conditions.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {installationSteps.map((item) => (
                    <div key={item.step} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary/20 border border-secondary flex items-center justify-center">
                        <span className="text-secondary font-bold text-sm">{item.step}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{item.title}</p>
                        <p className="text-muted-foreground text-xs">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-6">
                  We use <CitationLink href="https://masoncontractors.org/mcaa/" number={4}>Type S mortar per ASTM C270</CitationLink>, which handles below-grade and high-wind conditions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Retaining Walls Section with Image Placeholder */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center lg:[direction:rtl]">
              <div className="lg:[direction:ltr]">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Block Retaining Walls <span className="text-gradient-gold">Hold Back Soil</span> on North Jersey Slopes
                </h2>
                <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                  <p>
                    If you've got a sloped yard in North Jersey, you probably already know how water runoff and soil movement can cause headaches. Block retaining walls manage that runoff, prevent erosion, and create usable space on uneven lots.
                  </p>
                  <p>
                    There are four main types of retaining walls: gravity walls rely on their own weight; cantilever walls use a footing that extends under the soil; segmental walls are interlocking block systems for curved or terraced designs; anchored walls use tiebacks for extra support on steep slopes.
                  </p>
                  <p>
                    Hilly terrain across Morris and Passaic Counties makes retaining walls one of the most requested masonry services we handle. Walls over 4 feet typically need engineered plans per <CitationLink href="https://up.codes/viewer/new_jersey/ibc-2018/chapter/21/masonry" number={5}>NJ building code</CitationLink>.
                  </p>
                </div>
                <Link
                  to="/masonry-services/retaining-walls"
                  className="inline-flex items-center gap-2 text-secondary font-medium hover:gap-3 transition-all duration-300 mt-4"
                >
                  Learn more about our retaining wall services <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
              {/* Image Placeholder 3 */}
              <div className="lg:[direction:ltr] overflow-hidden rounded-xl border border-border bg-muted/20 aspect-[4/3]">
                <img src={retainingWallInstallImage} alt="Retaining wall installation on a wooded slope in Rutherford, NJ" width={800} height={600} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* Maintenance Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Routine Checks Prevent <span className="text-gradient-gold">Cracks and Water Damage</span> in Block Walls
              </h2>
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                <p>
                  Block walls are tough, but they're not maintenance-free. Catching small issues early is what keeps a repair bill from turning into a rebuild. The most common signs of trouble are stair-step cracks in the mortar joints, white powdery residue called efflorescence, leaning, or water seeping through.
                </p>
                <p>
                  Moisture absorption is one of the main drawbacks of block. Over time, freeze-thaw exposure in North Jersey wears mortar joints down faster than in milder climates. Tuckpointing (grinding out worn mortar and pressing in fresh material) can add decades to a wall's life. Waterproof coatings help too, especially on below-grade walls.
                </p>
                <p>
                  We recommend inspecting block walls twice a year: once in the spring after winter damage, and again in the fall before the next freeze cycle hits. Properly maintained block walls outlast many other building materials by a wide margin.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* YouTube Section */}
        <YouTubeShortsSection
          title="See Our"
          highlightedTitle="Block Work in Action"
          videoIds={youtubeVideoIds}
        />

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Frequently Asked <span className="text-gradient-gold">Questions</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="p-6 bg-card rounded-xl border border-border"
                >
                  <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <RelatedServices currentCategory="masonry" />

        {/* CTA Section */}
        <CTASection
          serviceName="Block Work"
          heading="Need Block Work in"
          highlightedHeading="North Jersey?"
        />

        {/* Citations */}
        <section className="py-10 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Accordion type="single" collapsible>
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                    Sources & Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                      {citations.map((citation) => (
                        <li key={citation.id}>
                          <a
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary hover:text-secondary/80 underline underline-offset-2"
                          >
                            {citation.source}
                          </a>{" "}
                          — {citation.description}
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default BlockWork;
